import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams, type SetURLSearchParams } from 'react-router-dom';

/** URL writes wait until typing pauses, matching the list query debounce (#4112). */
const WRITE_DELAY_MS = 300;

/**
 * Pending writes from every `useUrlSearchParam` on the page. One
 * `setSearchParams` commits them together: react-router applies the updater
 * to the search from the last render, so two calls in one turn would drop
 * whichever ran first.
 */
const pendingWrites = new Map<string, string>();
let writeTimer: ReturnType<typeof setTimeout> | null = null;
let immediateQueued = false;
let latestSetSearchParams: SetURLSearchParams | null = null;
let latestSearchParams: URLSearchParams | null = null;
let trackedPathname = '';
let listenerCount = 0;
/**
 * Writes handed to the router that no render has shown yet. react-router
 * applies navigations as transitions, so for a moment after a commit every
 * hook still renders the old search string. A sibling writer that builds its
 * next URL from that stale render would drop the just-committed param, and the
 * URL sync below would then clear the visible query.
 */
let inFlight: { fromSearch: string; writes: Map<string, string> } | null = null;

function applyWrites(prev: URLSearchParams, writes: ReadonlyMap<string, string>): URLSearchParams {
  const next = new URLSearchParams(prev);
  let changed = false;
  for (const [paramKey, value] of writes) {
    const current = next.get(paramKey) ?? '';
    if (current === value) continue;
    changed = true;
    if (value === '') next.delete(paramKey);
    else next.set(paramKey, value);
  }
  return changed ? next : prev;
}

/**
 * Re-apply writes this module has committed but the router has not rendered
 * yet. Other URL writers (list filters) pass the search they are about to
 * navigate with, so their navigation keeps the committed search param.
 * A no-op once the router has caught up.
 */
export function withCommittedUrlSearchParams(base: URLSearchParams): URLSearchParams {
  if (!inFlight || inFlight.fromSearch !== base.toString()) return base;
  return applyWrites(base, inFlight.writes);
}

function commitPending(): void {
  const setSearchParams = latestSetSearchParams;
  const baseline = latestSearchParams;
  if (!setSearchParams || !baseline || pendingWrites.size === 0) return;
  const writes = new Map(pendingWrites);
  pendingWrites.clear();
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  if (applyWrites(baseline, writes).toString() === baseline.toString()) return;
  inFlight = { fromSearch: baseline.toString(), writes };
  setSearchParams((prev) => applyWrites(prev, writes), { replace: true });
}

function scheduleCommit(): void {
  if (writeTimer !== null) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writeTimer = null;
    commitPending();
  }, WRITE_DELAY_MS);
}

/**
 * Commit at the end of the current task instead of after the typing delay.
 * A microtask, not a direct call, so several writes from one handler (a
 * select plus a pending search) still land in a single `setSearchParams`.
 */
function scheduleImmediateCommit(): void {
  if (writeTimer !== null) {
    clearTimeout(writeTimer);
    writeTimer = null;
  }
  if (immediateQueued) return;
  immediateQueued = true;
  queueMicrotask(() => {
    immediateQueued = false;
    commitPending();
  });
}

/**
 * Put a not-yet-committed search on the current history entry before a click
 * navigates away. `setSearchParams` is async and the following navigation
 * aborts it, so Back would return to the list without the search (#4112).
 */
function patchHistoryEntry(): void {
  if (pendingWrites.size === 0 || typeof window === 'undefined') return;
  if (window.location.pathname !== trackedPathname) return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const [paramKey, value] of pendingWrites) {
    const current = url.searchParams.get(paramKey) ?? '';
    if (current === value) continue;
    changed = true;
    if (value === '') url.searchParams.delete(paramKey);
    else url.searchParams.set(paramKey, value);
  }
  if (!changed) return;
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function onDocumentClickCapture(): void {
  patchHistoryEntry();
}

export interface UseUrlSearchParamOptions {
  /**
   * Write on the next microtask instead of after {@link WRITE_DELAY_MS}. For
   * discrete selections (status chip, select, view toggle) where there is no
   * typing to wait for (#4100). Writes still go through the shared batch.
   */
  immediate?: boolean;
}

/**
 * A search field or list filter backed by one URL search param (#4112, #4100).
 *
 * The returned value updates immediately. The param is written with
 * `replace` after {@link WRITE_DELAY_MS} (or at once with `immediate`), an
 * empty string removes it, and other params (status, tab, …) stay. A pending
 * write is flushed on unmount and patched onto the history entry when a click
 * leaves the page. Callers map their default value to `''` so the default
 * never shows in the URL.
 */
export function useUrlSearchParam(
  key = 'q',
  options: UseUrlSearchParamOptions = {},
): [string, (value: string) => void] {
  const immediate = options.immediate === true;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValueState] = useState(() => searchParams.get(key) ?? '');
  const keyRef = useRef(key);
  const pathnameRef = useRef(location.pathname);
  keyRef.current = key;
  pathnameRef.current = location.pathname;
  latestSetSearchParams = setSearchParams;
  latestSearchParams = searchParams;
  trackedPathname = location.pathname;
  if (inFlight && inFlight.fromSearch !== searchParams.toString()) inFlight = null;

  const setValue = useCallback((next: string) => {
    setValueState(next);
    pendingWrites.set(keyRef.current, next);
    if (immediate) scheduleImmediateCommit();
    else scheduleCommit();
  }, [immediate]);

  useEffect(() => {
    if (pendingWrites.has(key)) return;
    const fromUrl = searchParams.get(key) ?? '';
    setValueState((current) => (current === fromUrl ? current : fromUrl));
  }, [searchParams, key]);

  useEffect(() => {
    listenerCount += 1;
    if (listenerCount === 1) {
      document.addEventListener('click', onDocumentClickCapture, true);
    }
    return () => {
      const samePage = typeof window !== 'undefined'
        && window.location.pathname === pathnameRef.current;
      if (samePage) {
        commitPending();
      } else {
        pendingWrites.delete(keyRef.current);
        if (pendingWrites.size === 0 && writeTimer !== null) {
          clearTimeout(writeTimer);
          writeTimer = null;
        }
      }
      listenerCount -= 1;
      if (listenerCount === 0) {
        inFlight = null;
        document.removeEventListener('click', onDocumentClickCapture, true);
      }
    };
  }, []);

  return [value, setValue];
}
